import {
  GOVERNMENT_PORTAL_IMPERSONATE_USER_ID_HEADER,
  INSPECTION_PORTAL_IMPERSONATE_ACCOUNT_ID_HEADER,
  INSPECTION_PORTAL_IMPERSONATE_USER_ID_HEADER,
  OMT_AMS_PORTAL_IMPERSONATE_EMAIL_HEADER,
  REGISTRANT_PORTAL_IMPERSONATE_ACCOUNT_ID_HEADER,
  TITLE_PORTAL_IMPERSONATE_ACCOUNT_ID_HEADER,
} from "@/constants";
import type { AuthQuerySetup } from "@/contexts/auth-query-setup-context";
import { useSharedAuthQuerySetup } from "@/contexts/auth-query-setup-context";
import { client } from "@/hera-api/client/client.gen";
import {
  auth0LoginWebhookUrl,
  getHeraApiBaseUrl,
} from "@/lib/hera-api-base-url";
import { CustomFetchError } from "@/lib/utils";
import { sessionStorage } from "@/types/session-storage";
import { SESSION_KEYS } from "@/types/session-storage";
import { useAuth0 } from "@auth0/auth0-react";
import { QueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

function portalPathMatches(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Impersonation ids live in this tab's session storage for the whole tab.
 * Attach each one only while the matching portal is open. Operator pages must
 * stay unrestricted: a government-user id left over from the government portal
 * would otherwise lock licenses and the operator user list to that jurisdiction.
 */
export function mergePortalImpersonationHeaders(
  headers: Headers,
  pathname?: string,
): Headers {
  const path =
    pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  const h = new Headers(headers);
  const registrantAccountId = sessionStorage.get<string>(
    SESSION_KEYS.REGISTRANT_PORTAL_IMPERSONATE_ACCOUNT_ID,
    "",
  );
  if (registrantAccountId.trim() && portalPathMatches(path, "/registrant")) {
    h.set(
      REGISTRANT_PORTAL_IMPERSONATE_ACCOUNT_ID_HEADER,
      registrantAccountId.trim(),
    );
  }
  const onInspectionPortal = portalPathMatches(path, "/inspection");
  const inspectionAccountId = sessionStorage.get<string>(
    SESSION_KEYS.INSPECTION_PORTAL_IMPERSONATE_ACCOUNT_ID,
    "",
  );
  if (inspectionAccountId.trim() && onInspectionPortal) {
    h.set(
      INSPECTION_PORTAL_IMPERSONATE_ACCOUNT_ID_HEADER,
      inspectionAccountId.trim(),
    );
  }
  const inspectionUserId = sessionStorage.get<string>(
    SESSION_KEYS.INSPECTION_PORTAL_IMPERSONATE_USER_ID,
    "",
  );
  if (inspectionUserId.trim() && onInspectionPortal) {
    h.set(
      INSPECTION_PORTAL_IMPERSONATE_USER_ID_HEADER,
      inspectionUserId.trim(),
    );
  }
  const omtAmsEmail = sessionStorage.get<string>(
    SESSION_KEYS.OMT_AMS_PORTAL_IMPERSONATE_EMAIL,
    "",
  );
  if (omtAmsEmail.trim() && portalPathMatches(path, "/omt-ams")) {
    h.set(OMT_AMS_PORTAL_IMPERSONATE_EMAIL_HEADER, omtAmsEmail.trim());
  }
  const governmentUserId = sessionStorage.get<string>(
    SESSION_KEYS.GOVERNMENT_PORTAL_IMPERSONATE_USER_ID,
    "",
  );
  if (governmentUserId.trim() && portalPathMatches(path, "/government")) {
    h.set(
      GOVERNMENT_PORTAL_IMPERSONATE_USER_ID_HEADER,
      governmentUserId.trim(),
    );
  }
  const titleAccountId = sessionStorage.get<string>(
    SESSION_KEYS.TITLE_PORTAL_IMPERSONATE_ACCOUNT_ID,
    "",
  );
  if (titleAccountId.trim() && portalPathMatches(path, "/title")) {
    h.set(TITLE_PORTAL_IMPERSONATE_ACCOUNT_ID_HEADER, titleAccountId.trim());
  }
  return h;
}

/** Firefox rejects any body on GET and HEAD, including an empty buffer. */
export function methodForbidsRequestBody(method: string): boolean {
  const normalized = method.toUpperCase();
  return normalized === "GET" || normalized === "HEAD";
}

/**
 * Clone the request with merged headers without reusing Request.body streams.
 * Passing ReadableStream + duplex: "half" into fetch() caused net::ERR_ALPN_NEGOTIATION_FAILED
 * for some Chrome builds when calling localhost; materializing the body avoids that path.
 * Firefox reports a non-null body on GET requests that Chrome treats as empty, and then
 * throws if that buffer is copied onto a new GET Request.
 */
export async function rebuildRequestWithHeaders(
  source: Request,
  headers: Headers,
): Promise<Request> {
  const init: RequestInit = {
    method: source.method,
    headers,
    mode: source.mode,
    credentials: source.credentials,
    cache: source.cache,
    redirect: source.redirect,
    referrer: source.referrer,
    integrity: source.integrity,
  };
  if (methodForbidsRequestBody(source.method) || source.body === null) {
    return new Request(source.url, init);
  }
  const body = await source.clone().arrayBuffer();
  if (body.byteLength === 0) {
    return new Request(source.url, init);
  }
  return new Request(source.url, { ...init, body });
}

/**
 * The rebuilt request does not carry the caller's signal, so honour it here.
 * The server may still finish work the caller gave up on.
 */
export function rejectOnAbort<T>(
  promise: Promise<T>,
  signal: AbortSignal | null | undefined,
): Promise<T> {
  if (!signal) return promise;
  const abortError = () =>
    signal.reason ??
    new DOMException("The request was cancelled", "AbortError");
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (error: unknown) => {
        signal.removeEventListener("abort", onAbort);
        reject(error);
      },
    );
  });
}

/**
 * Deliberately untracked: this fetch carries every API call, including the
 * background refetches TanStack Query runs after an invalidation or on window
 * focus. Counting them here would light up the global request banner for work
 * the user never asked for. Foreground waits are reported by TanStack Query
 * itself (see `isForegroundQueryFetch`) and by the direct callers that opt in
 * with `trackServerRequest`.
 */
function createAuthRetryFetch(
  getAccessTokenSilently: (opts?: {
    authorizationParams?: { webhook_url: string };
  }) => Promise<string>,
  webhookUrl: string,
  onSessionExpired?: () => void,
): (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> {
  const defaultFetch = globalThis.fetch.bind(globalThis);
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const safeInit =
      init && methodForbidsRequestBody(init.method ?? "GET")
        ? { ...init, body: undefined }
        : init;
    const request =
      input instanceof Request ? input : new Request(input, safeInit);
    const requestForRetry = request.clone();
    const headers = mergePortalImpersonationHeaders(
      new Headers(request.headers),
    );
    const requestWithImpersonation = await rebuildRequestWithHeaders(
      request,
      headers,
    );
    let response = await rejectOnAbort(
      defaultFetch(requestWithImpersonation),
      request.signal,
    );
    if (response.status === 401) {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { webhook_url: webhookUrl },
        });
        client.setConfig({ auth: token });
        const newHeaders = mergePortalImpersonationHeaders(
          new Headers(requestForRetry.headers),
        );
        newHeaders.set("Authorization", `Bearer ${token}`);
        response = await defaultFetch(
          await rebuildRequestWithHeaders(requestForRetry, newHeaders),
        );
      } catch {
        onSessionExpired?.();
        // Return original 401 if refresh fails
      }
    }
    return response;
  };
}

function createAuthQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (error instanceof CustomFetchError) {
            // Don't retry if the error is a client error (4xx)
            if (error.response.status >= 400 && error.response.status < 500) {
              return false;
            }
          }
          return failureCount < 3;
        },
      },
    },
  });
}

/**
 * Placeholder for callers that read the portal's shared setup from context and
 * therefore never mount a client of their own.
 */
let unusedQueryClient: QueryClient | null = null;

function getUnusedQueryClient(): QueryClient {
  unusedQueryClient ??= createAuthQueryClient();
  return unusedQueryClient;
}

/** Registered on the module-scoped API client, so once is enough. */
let isErrorInterceptorRegistered = false;

/**
 * Runs the Auth0 wiring itself. `active` is false for the many components that
 * read the portal's shared setup from context, so their copies of this hook
 * stay inert instead of duplicating tokens, timers, and listeners.
 */
function useOwnAuthQuerySetup(active: boolean): AuthQuerySetup {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: isAuthLoading,
    error: authError,
  } = useAuth0();

  const [isAuthSetupComplete, setIsAuthSetupComplete] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const refreshIntervalIdRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const lastActivityRef = useRef<number>(0);
  const [queryClient] = useState(() =>
    active ? createAuthQueryClient() : getUnusedQueryClient(),
  );

  useEffect(() => {
    if (!active) return;

    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };
    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ] as const;
    for (const ev of activityEvents) {
      window.addEventListener(ev, updateActivity, { passive: true });
    }

    const setupAuth = async () => {
      if (!isAuthenticated || isAuthLoading || authError) {
        setIsAuthSetupComplete(false);
        return;
      }

      try {
        const webhookUrl = auth0LoginWebhookUrl();
        const accessToken = await getAccessTokenSilently({
          authorizationParams: { webhook_url: webhookUrl },
        });

        const authRetryFetch = createAuthRetryFetch(
          getAccessTokenSilently,
          webhookUrl,
          () => setSessionExpired(true),
        );
        client.setConfig({
          auth: accessToken,
          baseUrl: getHeraApiBaseUrl(),
          fetch: authRetryFetch,
        });

        if (!isErrorInterceptorRegistered) {
          isErrorInterceptorRegistered = true;
          client.interceptors.error.use(async (error, response) => {
            if (response && !response.ok) {
              return new CustomFetchError(error, response);
            }

            return error;
          });
        }

        setSessionExpired(false);
        setIsAuthSetupComplete(true);

        // Proactively refresh token so requests don't hit 401 (only when user has been active)
        const refreshIntervalMs = 15 * 60 * 1000; // 15 minutes
        lastActivityRef.current = Date.now();

        const refreshToken = async () => {
          if (Date.now() - lastActivityRef.current > refreshIntervalMs) {
            return; // Skip refresh when idle for 15+ minutes
          }
          try {
            const token = await getAccessTokenSilently({
              authorizationParams: { webhook_url: webhookUrl },
            });
            client.setConfig({ auth: token });
          } catch {
            // Ignore; 401 retry will handle the next request if needed
          }
        };

        refreshIntervalIdRef.current = setInterval(
          refreshToken,
          refreshIntervalMs,
        );
      } catch (err) {
        setIsAuthSetupComplete(false);
        console.error("Auth setup failed: could not get access token.", err);
      }
    };

    setupAuth();
    return () => {
      for (const ev of activityEvents) {
        window.removeEventListener(ev, updateActivity);
      }
      if (refreshIntervalIdRef.current !== null) {
        clearInterval(refreshIntervalIdRef.current);
        refreshIntervalIdRef.current = null;
      }
    };
  }, [
    active,
    getAccessTokenSilently,
    isAuthenticated,
    isAuthLoading,
    authError,
  ]);

  return {
    queryClient,
    isAuthLoading,
    authError,
    isAuthenticated,
    isAuthSetupComplete,
    sessionExpired,
  };
}

export function useAuthQuerySetup(): AuthQuerySetup {
  const shared = useSharedAuthQuerySetup();
  const own = useOwnAuthQuerySetup(shared === null);
  return shared ?? own;
}
